/** Collapsible sidebar groups for hospital admin (and chain admin hospital section). */

export type NavLink = { name: string; path: string };
export type NavGroup = { name: string; kind: 'group'; children: NavLink[] };

const g = (name: string, children: NavLink[]): NavGroup => ({ name, kind: 'group', children });

export const chainManagementSidebarGroup = g('Chain management', [
  { name: 'Chain Dashboard', path: '/hospital-admin/chain/dashboard' },
  { name: 'My Hospitals', path: '/hospital-admin/chain/hospitals' },
  { name: 'Admins', path: '/hospital-admin/chain/admins' },
  { name: 'Meta Integration', path: '/hospital-admin/marketing/meta-integration' },
  { name: 'Google Integration', path: '/hospital-admin/marketing/google-integration' },
]);

export const partnersSidebarGroup = g('Partners', [
  { name: 'Analytics', path: '/hospital-admin/partners/analytics' },
  { name: 'Partner approvals', path: '/hospital-admin/partners/approvals' },
  { name: 'Partner leads', path: '/hospital-admin/partners/leads' },
  { name: 'Partner management', path: '/hospital-admin/partners/management' },
]);

export const hospitalAdminSidebarGroups: NavGroup[] = [
  g('Overview', [
    { name: 'Dashboard', path: '/hospital-admin/dashboard' },
    { name: 'Queue Management', path: '/hospital-admin/queue-management' },
    { name: 'IPD Dashboard', path: '/hospital-admin/ipd-dashboard' },
  ]),
  g('Patients & access', [
    { name: 'Manage Patients', path: '/hospital-admin/patients' },
    { name: 'Package due amounts', path: '/hospital-admin/patient-due-balances' },
    { name: 'Appointments', path: '/hospital-admin/appointments' },
    { name: 'Self Registration QR', path: '/hospital-admin/self-register-qr' },
  ]),
  g('Inpatient & day care', [
    { name: 'IPD Queue', path: '/hospital-admin/ipd-queue' },
    { name: 'IPD Offline Billing', path: '/hospital-admin/ipd-offline-queue' },
    { name: 'IPD Pending Collection', path: '/hospital-admin/ipd-pending-collection' },
    { name: 'IPD approval requests', path: '/hospital-admin/ipd-bill-edit-requests' },
    { name: 'IPD excess collection', path: '/hospital-admin/ipd-excess-collection' },
    { name: 'Day Care', path: '/hospital-admin/daycare' },
  ]),
  g('Hospital setup', [
    { name: 'Day Care setup', path: '/hospital-admin/daycare-setup' },
    { name: 'Department Management', path: '/hospital-admin/department' },
    { name: 'Ward Management', path: '/hospital-admin/ward-management' },
    { name: 'Insurance Management', path: '/hospital-admin/insurance-management' },
  ]),
  g('Staff & scheduling', [
    { name: 'User Management', path: '/hospital-admin/users' },
    { name: 'Shift Management', path: '/hospital-admin/shifts' },
    { name: 'Doctor OPD Fees', path: '/hospital-admin/opd-fees' },
  ]),
  partnersSidebarGroup,
  g('CRM & leads', [
    { name: 'Leads', path: '/hospital-admin/leads' },
    { name: 'Lead Insights', path: '/hospital-admin/lead-insights' },
    { name: 'IPD Cases', path: '/hospital-admin/crm-ipd-cases' },
    { name: 'IPD Admitted', path: '/hospital-admin/crm-ipd-admitted' },
    { name: 'Mini CRM', path: '/hospital-admin/mini-crm-settings' },
    { name: 'Sarvam integrations', path: '/hospital-admin/integrations/sarvam' },
    { name: 'Social Inbox', path: '/hospital-admin/social/inbox' },
    { name: 'Instagram settings', path: '/hospital-admin/social/settings' },
    { name: 'Instagram analytics', path: '/hospital-admin/social/analytics' },
    { name: 'WhatsApp Campaigns', path: '/hospital-admin/whatsapp-campaigns' },
    { name: 'Media Library', path: '/hospital-admin/whatsapp-media-library' },
  ]),
  g('Marketing', [
    { name: 'Meta Dashboard', path: '/hospital-admin/marketing/meta-dashboard' },
    { name: 'Meta Event Logs', path: '/hospital-admin/marketing/meta-event-logs' },
    { name: 'Google Event Logs', path: '/hospital-admin/marketing/google-event-logs' },
    { name: 'Meta Attribution', path: '/hospital-admin/marketing/meta-attribution' },
  ]),
  g('Engagement', [
    { name: 'Loyalty Program', path: '/hospital-admin/loyalty' },
    { name: 'App analytics', path: '/hospital-admin/loyalty/app-analytics' },
    { name: 'TrueCare welcome WhatsApp', path: '/hospital-admin/loyalty/truecare-welcome' },
  ]),
  g('Finance', [
    { name: 'GST Management', path: '/hospital-admin/gst-management' },
    { name: 'Expenses', path: '/hospital-admin/expenses' },
    { name: 'Lab Payout', path: '/hospital-admin/lab-payout' },
    { name: 'Pharmacy Vendors', path: '/hospital-admin/pharmacy-vendors' },
    { name: 'Additional Revenue', path: '/hospital-admin/additional-revenue' },
    { name: 'Receivables', path: '/hospital-admin/receivables' },
    { name: 'Accounting', path: '/hospital-admin/accounting' },
    { name: 'Vendor payments', path: '/hospital-admin/vendor-payments' },
    { name: 'Reconciliation', path: '/hospital-admin/reconciliation' },
  ]),
  g('Reports & intelligence', [
    { name: 'Command Centre', path: '/hospital-admin/intelligence' },
    { name: 'Business Reports', path: '/hospital-admin/business-reports' },
    { name: 'Pending Payments', path: '/hospital-admin/pending-payments' },
    { name: 'Operational Reports', path: '/hospital-admin/operational-reports' },
    { name: 'Customer Voice', path: '/hospital-admin/customer-voice' },
    { name: 'Patient-wise Bills', path: '/hospital-admin/patient-wise-bills' },
    { name: 'Lab Reports', path: '/hospital-admin/lab-reports' },
    { name: 'External Lab Vendors', path: '/hospital-admin/lab-external-vendors' },
    { name: 'IPD Lab Tests', path: '/hospital-admin/ipd-lab-tests' },
    { name: 'Pharmacy Bills Report', path: '/hospital-admin/pharmacy-bills-report' },
    { name: 'Website analytics', path: '/hospital-admin/website-analytics' },
  ]),
  g('Compliance', [{ name: 'Audit trail', path: '/hospital-admin/audit' }]),
];

/** Business analyst: subset of hospital admin modules. */
export const businessAnalystSidebarGroups: NavGroup[] = [
  g('Inpatient & day care', [
    { name: 'IPD Queue', path: '/hospital-admin/ipd-queue' },
    { name: 'IPD Offline Billing', path: '/hospital-admin/ipd-offline-queue' },
    { name: 'IPD Pending Collection', path: '/hospital-admin/ipd-pending-collection' },
    { name: 'IPD approval requests', path: '/hospital-admin/ipd-bill-edit-requests' },
    { name: 'IPD excess collection', path: '/hospital-admin/ipd-excess-collection' },
    { name: 'Day Care', path: '/hospital-admin/daycare' },
  ]),
  partnersSidebarGroup,
  g('CRM & leads', [
    { name: 'Leads', path: '/hospital-admin/leads' },
    { name: 'Lead Insights', path: '/hospital-admin/lead-insights' },
    { name: 'IPD Cases', path: '/hospital-admin/crm-ipd-cases' },
    { name: 'IPD Admitted', path: '/hospital-admin/crm-ipd-admitted' },
    { name: 'Mini CRM', path: '/hospital-admin/mini-crm-settings' },
    { name: 'Sarvam integrations', path: '/hospital-admin/integrations/sarvam' },
    { name: 'Social Inbox', path: '/hospital-admin/social/inbox' },
    { name: 'Instagram settings', path: '/hospital-admin/social/settings' },
    { name: 'Instagram analytics', path: '/hospital-admin/social/analytics' },
    { name: 'WhatsApp Campaigns', path: '/hospital-admin/whatsapp-campaigns' },
    { name: 'Media Library', path: '/hospital-admin/whatsapp-media-library' },
  ]),
  g('Finance', [
    { name: 'GST Management', path: '/hospital-admin/gst-management' },
    { name: 'Expenses', path: '/hospital-admin/expenses' },
    { name: 'Lab Payout', path: '/hospital-admin/lab-payout' },
    { name: 'Pharmacy Vendors', path: '/hospital-admin/pharmacy-vendors' },
    { name: 'Vendor payments', path: '/hospital-admin/vendor-payments' },
    { name: 'Reconciliation', path: '/hospital-admin/reconciliation' },
  ]),
  g('Reports & intelligence', [
    { name: 'Command Centre', path: '/hospital-admin/intelligence' },
    { name: 'Business Reports', path: '/hospital-admin/business-reports' },
    { name: 'Pending Payments', path: '/hospital-admin/pending-payments' },
    { name: 'Operational Reports', path: '/hospital-admin/operational-reports' },
    { name: 'Customer Voice', path: '/hospital-admin/customer-voice' },
    { name: 'Patient-wise Bills', path: '/hospital-admin/patient-wise-bills' },
    { name: 'Lab Reports', path: '/hospital-admin/lab-reports' },
    { name: 'External Lab Vendors', path: '/hospital-admin/lab-external-vendors' },
    { name: 'IPD Lab Tests', path: '/hospital-admin/ipd-lab-tests' },
    { name: 'Pharmacy Bills Report', path: '/hospital-admin/pharmacy-bills-report' },
    { name: 'Website analytics', path: '/hospital-admin/website-analytics' },
  ]),
];
